/**
 * Sprachen im Panel: die Vue-Anbindung.
 *
 * Im Template:      {{ $t('layout.menu.dashboard') }}
 *                   <p v-html="$th('auth.verify.sent', {email})" />
 * Im <script setup>: const {t, locale, setLocale} = useI18n();
 * Daten und Zahlen:  formatDate(value, options), formatNumber(value, options)
 *
 * **Woher die Sprache kommt.** Gespeicherte Wahl (localStorage), sonst
 * navigator.languages, sonst Deutsch. Kein Cookie -- das Backend erfährt
 * die Sprache über den Header X-Locale, den jeder Request mitschickt:
 * axios über einen Interceptor, Inertia über das before-Ereignis. Beim
 * allerersten Aufruf gibt es noch keinen Header, das Backend nimmt dann
 * Accept-Language. Weicht die gespeicherte Wahl davon ab, lädt die Seite
 * einmal nach, damit auch Seitentitel und Meldungen vom Server passen.
 *
 * @see ./core.mjs
 * @see \App\Http\Middleware\SetLocale
 * @see lang/README.md
 */
import {computed, ref} from 'vue';
import {Inertia} from '@inertiajs/inertia';
import {
    DEFAULT_LOCALE, EDITOR_TRANSLATION_LOCALES, LOCALES, LOCALE_HEADER, LOCALE_LABELS, LOCALE_NAMES, LOCALE_TAGS, VUETIFY_LOCALES,
    collectMessages, createTranslator, detectLocale, normalizeLocale, readStoredLocale, writeStoredLocale,
} from './core.mjs';
import {localizeEditorItem as localizeItem, translateAppTextFrom} from './appEditorTexts.mjs';

const messages = collectMessages(import.meta.glob('../../../lang/*/ui/*.json', {eager: true}));

const storage = typeof window !== 'undefined' ? window.localStorage : null;

const locale = ref(detectLocale({
    stored: readStoredLocale(storage),
    languages: typeof navigator !== 'undefined' ? navigator.languages : [],
}));

const reported = new Set();
const translator = createTranslator(messages, () => locale.value, {
    onMissing(key, missingIn) {
        if (!import.meta.env.DEV || reported.has(`${missingIn}:${key}`)) return;
        reported.add(`${missingIn}:${key}`);
        console.warn(`[i18n] Schlüssel "${key}" fehlt in ${missingIn}`);
    },
});

/*
 * Reaktiv, ohne dass jemand daran denken muss: Der Übersetzer liest bei
 * jedem Aufruf locale.value. Ein Template oder computed, das t() aufruft,
 * hängt damit an der Sprache und rendert beim Wechsel neu.
 */
export const t = translator.t;
export const th = translator.th;

export const localeTag = computed(() => LOCALE_TAGS[locale.value]);

/** Datum und Uhrzeit in der Sprache des Panels, nicht fest de-DE. */
export function formatDate(value, options = {}) {
    if (value === null || value === undefined || value === '') return '';
    const date = value instanceof Date ? value : new Date(value);
    if (Number.isNaN(date.getTime())) return String(value);

    return new Intl.DateTimeFormat(localeTag.value, options).format(date);
}

export function formatNumber(value, options = {}) {
    return new Intl.NumberFormat(localeTag.value, options).format(value);
}

// ----------------------------------------------------------------------------
// Beschriftungen der App-Editoren
//
// Ein Wörterbuch nach deutschem Text, siehe ./appEditorTexts.mjs. Es wird
// erst geladen, wenn eine andere Sprache als Deutsch gilt -- deutsche Nutzer
// laden es nie. Bis es da ist, stehen die Texte wie gespeichert da.
// ----------------------------------------------------------------------------
const appEditorLoaders = import.meta.glob('../../../lang/*/app_editors/*.json');
const appEditorDictionaries = ref({});
const appEditorLoading = new Map();

export {EDITOR_TRANSLATION_LOCALES};

function loadAppEditorTexts(value) {
    if (value === DEFAULT_LOCALE || appEditorDictionaries.value[value]) return Promise.resolve();
    if (!appEditorLoading.has(value)) {
        appEditorLoading.set(value, fetchAppEditorTexts(value).finally(() => appEditorLoading.delete(value)));
    }

    return appEditorLoading.get(value);
}

async function fetchAppEditorTexts(value) {
    const parts = await Promise.all(
        Object.entries(appEditorLoaders)
            .filter(([path]) => path.includes(`/lang/${value}/app_editors/`))
            .map(([, load]) => load())
    );

    // Neues Objekt statt Zuweisung in das alte: sonst merkt die Reaktivität nichts.
    appEditorDictionaries.value = {
        ...appEditorDictionaries.value,
        [value]: Object.assign({}, ...parts.map(part => part.default ?? part)),
    };
}

/**
 * Das gemeinsame Wörterbuch einer Sprache, reaktiv. Lädt es beim ersten
 * Aufruf nach, bis dahin null. Gebraucht auf der Seite einer App, die es
 * auch dann zeigt, wenn das Panel auf Deutsch steht.
 */
export function appEditorDictionary(value) {
    if (value === DEFAULT_LOCALE) return null;
    if (!appEditorDictionaries.value[value]) loadAppEditorTexts(value);

    return appEditorDictionaries.value[value] ?? null;
}

/**
 * Ein Text eines App-Editors in der Sprache des Panels.
 *
 * @param {string} text
 * @param {object|null} own  die eigenen Übersetzungen der App
 *                           (applications.editor_translations), sie gehen vor
 */
export function appText(text, own = null) {
    return translateAppTextFrom([own?.[locale.value], appEditorDictionaries.value[locale.value]], text);
}

/** Ein Eintrag der Editor-Definition, für `v-bind` an ein Feld. */
export function localizeEditorItem(item, own = null) {
    return locale.value === DEFAULT_LOCALE ? item : localizeItem(item, text => appText(text, own));
}

let vuetifyInstance = null;

/** In welcher Sprache die zuletzt angezeigte Seite vom Server kam. */
let serverLocale = null;

/**
 * Schon einmal für diese Seite nachgeladen? Übernimmt der Server den
 * Header nicht, bliebe die Sprache ungleich -- ohne diese Sperre würde
 * endlos nachgeladen.
 */
let reloadPending = false;

function apply(value) {
    if (typeof document !== 'undefined') {
        document.documentElement.lang = LOCALE_TAGS[value];
    }
    if (vuetifyInstance) {
        vuetifyInstance.locale.current.value = VUETIFY_LOCALES[value];
    }
}

/**
 * Stimmt die Sprache der Serverantwort nicht mit der gewählten überein,
 * einmal nachladen. Die Texte aus lang/*\/ui wechseln sofort, aber
 * Seitentitel und Meldungen kommen vom Server.
 *
 * **Nie vor dem ersten navigate-Ereignis.** Bis dahin kennt Inertia seine
 * Seite noch nicht, der Request ginge ohne X-Inertia-Version raus, der
 * Server antwortete mit 409, und Inertia lüde die ganze Seite neu -- die
 * dann wieder abweicht. Das war eine Endlosschleife, am 28.09.2026 gleich
 * beim ersten Test.
 */
function syncServer() {
    if (serverLocale === null || serverLocale === locale.value || reloadPending) return;

    reloadPending = true;
    Inertia.reload({preserveState: true, preserveScroll: true});
}

export function setLocale(value) {
    const next = normalizeLocale(value) ?? DEFAULT_LOCALE;

    locale.value = next;
    writeStoredLocale(storage, next);
    apply(next);
    loadAppEditorTexts(next);

    // Eine neue Wahl ist ein neuer Anlass, auch wenn vorher schon nachgeladen wurde.
    reloadPending = false;
    syncServer();
}

export function useI18n() {
    return {
        t,
        th,
        locale: computed(() => locale.value),
        locales: LOCALES.map(value => ({value, label: LOCALE_LABELS[value], name: LOCALE_NAMES[value], tag: LOCALE_TAGS[value]})),
        setLocale,
        formatDate,
        formatNumber,
        appText,
        localizeEditorItem,
    };
}

/**
 * Den Header an eine axios-Instanz hängen. Gebraucht für die globale
 * Instanz und für services/api.js, das eine eigene anlegt.
 */
export function attachLocaleHeader(instance) {
    instance.interceptors.request.use((config) => {
        config.headers = config.headers ?? {};
        config.headers[LOCALE_HEADER] = locale.value;
        return config;
    });
}

export default {
    /**
     * @param app
     * @param {{vuetify: object}} options
     */
    install(app, {vuetify} = {}) {
        vuetifyInstance = vuetify ?? null;

        app.config.globalProperties.$t = t;
        app.config.globalProperties.$th = th;
        app.config.globalProperties.$formatDate = formatDate;

        /*
         * Inertia bringt sein eigenes axios mit (node_modules/@inertiajs/
         * inertia/node_modules/axios), ein Interceptor an unserem käme dort
         * nicht an. Das before-Ereignis reicht das headers-Objekt des
         * Besuchs als Referenz durch, und Inertia verwendet genau dieses.
         */
        Inertia.on('before', (event) => {
            event.detail.visit.headers[LOCALE_HEADER] = locale.value;
        });
        /*
         * Nach jeder Serverantwort nachsehen, in welcher Sprache sie kam.
         *
         * Zwei Ereignisse, weil keines allein reicht: `navigate` kommt für
         * die erste Seite, die mit dem HTML geliefert wird -- erst dann kennt
         * Inertia sie, siehe syncServer(). Bei einem Besuch derselben URL
         * aber, und genau das ist Inertia.reload(), ersetzt Inertia den
         * Verlaufseintrag und feuert `navigate` nicht (0.11, `a||m(e)`).
         * Dafür kommt `success` nach jedem erfolgreichen Besuch. Ohne das
         * hielt das Modul den Server nach dem Nachladen weiter für die alte
         * Sprache, und der Wechsel zurück lud nicht mehr nach.
         */
        const onServerPage = (page) => {
            serverLocale = normalizeLocale(page?.props?.locale);

            if (serverLocale === locale.value) {
                reloadPending = false;
                return;
            }

            // Nicht mitten im Seitenwechsel einen neuen anstoßen.
            setTimeout(syncServer, 0);
        };
        Inertia.on('navigate', (event) => onServerPage(event.detail.page));
        Inertia.on('success', (event) => onServerPage(event.detail.page));

        /*
         * Gespeichert wird hier nichts: Ohne ausdrückliche Wahl soll die
         * Sprache des Systems gelten, auch wenn sie sich später ändert.
         * Erst setLocale() schreibt in den localStorage.
         */
        apply(locale.value);
        loadAppEditorTexts(locale.value);
    },
};
